import { custom } from "./actionsUtilities";
import { success } from "./actionsUtilities";
import { storeService } from "../../services";
import {
  customToast,
  getProductLabelNotifications,
  handleEditStoreClick,
} from "../../shared/utility";
import ToastPrintLabel from "../../shared/components/ToastPrintLabel/ToastPrintLabel";
import {
  toastType,
  toastMessages,
  storeConstants,
  systemConstants,
  dashboardConstants,
  routesPathConstants,
  sessionStorageConstants,
  EMPTY_STRING,
} from "../../constants";
import {
  isItemInStorage,
  setItemToStorage,
  getItemFromStorage,
} from "../../system/storage";

const getStores = (user) => {
  return (dispatch) => {
    dispatch(
      custom(storeConstants.GET_STORES, systemConstants.SPINNER_ACTIVATE)
    );
    storeService
      .getStores()
      .then(
        (response) => {
          dispatch(success(response, storeConstants.GET_STORES));

          let validStoreIndex = 0;

          // Check if user's store exists in the response
          const userDefaultStoreIndex = response.findIndex(
            (store) => store?.id === user?.defaultStore?.id
          );

          if (userDefaultStoreIndex >= 0) {
            validStoreIndex = userDefaultStoreIndex; // Use user's store index if found
          }

          // Check if a valid index is in session storage
          if (isItemInStorage(sessionStorageConstants.STORE_INDEX)) {
            const storedIndex = Number(
              getItemFromStorage(sessionStorageConstants.STORE_INDEX)
            );

            if (storedIndex >= 0 && storedIndex < response.length) {
              validStoreIndex = storedIndex; // Use stored index if valid
            } else {
              setItemToStorage(
                sessionStorageConstants.STORE_INDEX,
                validStoreIndex
              ); // Reset to valid index if stored index is invalid
            }
          } else {
            setItemToStorage(
              sessionStorageConstants.STORE_INDEX,
              validStoreIndex
            ); // Store the valid index if none is in session storage
          }

          // Set the current store based on the valid index
          dispatch(
            success(response[validStoreIndex], storeConstants.SET_CURRENT_STORE)
          );
        },
        (error) => {
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(storeConstants.GET_STORES, systemConstants.SPINNER_DEACTIVATE)
        );
      });
  };
};

const addStore = (data) => {
  return (dispatch) => {
    dispatch(
      custom(storeConstants.ADD_STORE, systemConstants.SPINNER_ACTIVATE)
    );
    storeService
      .addStore(data)
      .then(
        (response) => {
          dispatch(getStores(EMPTY_STRING));

          dispatch(
            custom(
              routesPathConstants.ADMIN_STORES,
              systemConstants.TOGGLE_REDIRECT_PAGE
            )
          );
          customToast(
            toastMessages.STORE_ADDED_SUCCESSFULLY,
            toastType.SUCCESS
          );
        },
        (error) => {
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(storeConstants.ADD_STORE, systemConstants.SPINNER_DEACTIVATE)
        );
      });
  };
};

const editStore = (data) => {
  return (dispatch) => {
    dispatch(
      custom(storeConstants.ADD_STORE, systemConstants.SPINNER_ACTIVATE)
    );
    storeService
      .editStore(data)
      .then(
        (response) => {
          dispatch(
            custom(
              routesPathConstants.ADMIN_STORES,
              systemConstants.TOGGLE_REDIRECT_PAGE
            )
          );
          customToast(
            toastMessages.STORE_UPDATED_SUCCESSFULLY,
            toastType.SUCCESS
          );
        },
        (error) => {
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(storeConstants.ADD_STORE, systemConstants.SPINNER_DEACTIVATE)
        );
      });
  };
};

const getStoreOwners = (userId) => {
  return (dispatch) => {
    dispatch(
      custom(storeConstants.GET_STORE_OWNERS, systemConstants.SPINNER_ACTIVATE)
    );
    storeService
      .getStoreOwners(userId)
      .then(
        (response) => {
          dispatch(success(response, storeConstants.GET_STORE_OWNERS));
        },
        (error) => {
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(
            storeConstants.GET_STORE_OWNERS,
            systemConstants.SPINNER_DEACTIVATE
          )
        );
      });
  };
};

const deleteStore = (storeId) => {
  return (dispatch) => {
    dispatch(
      custom(storeConstants.DELETE_STORE, systemConstants.SPINNER_ACTIVATE)
    );
    storeService
      .deleteStore(storeId)
      .then(
        (response) => {
          setItemToStorage(sessionStorageConstants.STORE_INDEX, 0);
          dispatch(getStores(EMPTY_STRING));
          customToast(
            toastMessages.STORE_DELETED_SUCCESSFULLY,
            toastType.SUCCESS
          );
        },
        (error) => {
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(
            storeConstants.DELETE_STORE,
            systemConstants.SPINNER_DEACTIVATE
          )
        );
      });
  };
};

const uploadLogo = (id, logoPath, data) => {
  return (dispatch) => {
    dispatch(
      custom(storeConstants.UPLOAD_LOGO, systemConstants.SPINNER_ACTIVATE)
    );
    storeService
      .uploadLogo(logoPath)
      .then(
        (response) => {
          const filePath = response?.data?.data;
          const receiptData = { ...data, logo: filePath };
          dispatch(updateReceiptDetails(id, receiptData));
        },
        (error) => {
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(storeConstants.UPLOAD_LOGO, systemConstants.SPINNER_DEACTIVATE)
        );
      });
  };
};

const deleteBlobByUrl = (url) => {
  return (dispatch) => {
    dispatch(
      custom(storeConstants.DELETE_IMAGE, systemConstants.SPINNER_ACTIVATE)
    );
    storeService
      .deleteBlobByUrl(url)
      .then(
        (response) => {
          customToast(toastMessages.DELETED_SUCCESSFULLY, toastType.SUCCESS);
        },
        (error) => {
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(
            storeConstants.DELETE_IMAGE,
            systemConstants.SPINNER_DEACTIVATE
          )
        );
      });
  };
};

const updateReceiptDetails = (id, data) => {
  return (dispatch) => {
    dispatch(
      custom(
        storeConstants.UPDATE_RECEIPT_DETAILS,
        systemConstants.SPINNER_ACTIVATE
      )
    );
    storeService
      .updateReceiptDetails(id, data)
      .then(
        (response) => {
          dispatch(getStores(EMPTY_STRING));
          customToast(
            toastMessages.RECEIPT_DETAILS_UPDATED_SUCCESSFULLY,
            toastType.SUCCESS
          );
        },
        (error) => {
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(
            storeConstants.UPDATE_RECEIPT_DETAILS,
            systemConstants.SPINNER_DEACTIVATE
          )
        );
      });
  };
};

const updateStoreSettings = (id, data, macAddress, resetForm) => {
  return (dispatch) => {
    dispatch(
      custom(
        storeConstants.UPDATE_RECEIPT_DETAILS,
        systemConstants.SPINNER_ACTIVATE
      )
    );
    storeService
      .updateStoreSettings(id, data, macAddress)
      .then(
        (response) => {
          dispatch(getStores(EMPTY_STRING));
        },
        (error) => {
          resetForm();
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(
            storeConstants.UPDATE_RECEIPT_DETAILS,
            systemConstants.SPINNER_DEACTIVATE
          )
        );
      });
  };
};

const getUserNotifications = (
  storeId,
  inventoryUpdateNotification = false,
  message = "",
  isUpdate = false,
  isPrintLabel = true
) => {
  return (dispatch) => {
    storeService.getUserNotifications(storeId).then(
      (response) => {
        dispatch(success(response, storeConstants.GET_USER_NOTIFICATIONS));
        if (inventoryUpdateNotification) {
          const notificationMessage =
            getProductLabelNotifications(response)[0]?.message ?? EMPTY_STRING;
          customToast(
            <span className="d-flex flex-column gap-2">
              {isUpdate || !isPrintLabel ? message : notificationMessage}
              {isPrintLabel ? <ToastPrintLabel /> : ""}
            </span>,
            toastType.SUCCESS,
            7000
          );
        }
      },
      (error) => {
        customToast(error, toastType.ERROR);
        dispatch(success([], storeConstants.GET_USER_NOTIFICATIONS));
      }
    );
  };
};

const markNotificationAsRead = (storeId, data) => {
  return (dispatch) => {
    dispatch(
      custom(
        storeConstants.GET_USER_NOTIFICATIONS,
        systemConstants.SPINNER_ACTIVATE
      )
    );
    storeService
      .markNotificationAsRead(storeId, data)
      .then(
        (response) => {
          dispatch(getUserNotifications(storeId));
        },
        (error) => {
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(
            storeConstants.GET_USER_NOTIFICATIONS,
            systemConstants.SPINNER_DEACTIVATE
          )
        );
      });
  };
};

const createNotification = (storeId, data) => {
  return (dispatch) => {
    dispatch(
      custom(
        storeConstants.CREATE_NOTIFICATION,
        systemConstants.SPINNER_ACTIVATE
      )
    );
    storeService
      .createNotification(storeId, data)
      .then(
        (response) => {
          // dispatch(getUserNotifications(storeId));
          dispatch(
            success(response?.data?.data, storeConstants.CREATE_NOTIFICATION)
          );
        },
        (error) => {
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(
            storeConstants.CREATE_NOTIFICATION,
            systemConstants.SPINNER_DEACTIVATE
          )
        );
      });
  };
};

const getPayoutsInformation = (storeId, startDate, endDate) => {
  return (dispatch) => {
    dispatch(
      custom(
        storeConstants.PAYOUT_INFORMATION,
        systemConstants.SPINNER_ACTIVATE
      )
    );
    storeService
      .getPayoutsInformation(storeId, startDate, endDate)
      .then(
        (response) => {
          dispatch(success(response, storeConstants.PAYOUT_INFORMATION));
        },
        (error) => {
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(
            storeConstants.PAYOUT_INFORMATION,
            systemConstants.SPINNER_DEACTIVATE
          )
        );
      });
  };
};

const getCustomSalesTaxRules = (storeId) => {
  return (dispatch) => {
    dispatch(
      custom(
        storeConstants.GET_CUSTOM_TAX_RULES,
        systemConstants.SPINNER_ACTIVATE
      )
    );
    storeService
      .getCustomSalesTaxRules(storeId)
      .then(
        (response) => {
          dispatch(success(response, storeConstants.GET_CUSTOM_TAX_RULES));
        },
        (error) => {
          customToast(error, toastType.ERROR);
          dispatch(success([], storeConstants.GET_CUSTOM_TAX_RULES));
        }
      )
      .finally(() => {
        dispatch(
          custom(
            storeConstants.GET_CUSTOM_TAX_RULES,
            systemConstants.SPINNER_DEACTIVATE
          )
        );
      });
  };
};
const addSaleTax = (
  storeId,
  data,
  queryParams = EMPTY_STRING,
  toggleAddCustomSalesTaxRuleModal
) => {
  return (dispatch) => {
    dispatch(
      custom(storeConstants.ADD_SALE_TAX, systemConstants.SPINNER_ACTIVATE)
    );
    storeService
      .addSaleTax(storeId, data, queryParams)
      .then(
        (response) => {
          customToast(toastMessages.SALE_TAX_ADDED, toastType.SUCCESS);
          toggleAddCustomSalesTaxRuleModal(false);
          dispatch(getCustomSalesTaxRules(storeId));
        },
        (error) => {
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(
            storeConstants.ADD_SALE_TAX,
            systemConstants.SPINNER_DEACTIVATE
          )
        );
      });
  };
};

const editSaleTax = (
  ObjectDetail,
  data,
  queryParams = EMPTY_STRING,
  toggleAddCustomSalesTaxRuleModal
) => {
  return (dispatch) => {
    dispatch(
      custom(storeConstants.UPDATE_SALE_TAX, systemConstants.SPINNER_ACTIVATE)
    );
    storeService
      .editSaleTax(ObjectDetail, data, queryParams)
      .then(
        (response) => {
          customToast(toastMessages.SALE_TAX_UPDATED, toastType.SUCCESS);
          toggleAddCustomSalesTaxRuleModal(false);
          dispatch(
            storeActions.getPayoutsInformation(
              ObjectDetail.storeId,
              ObjectDetail.startDate,
              ObjectDetail.endDate
            )
          );
          dispatch(getCustomSalesTaxRules(ObjectDetail.storeId));
        },
        (error) => {
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(
            dashboardConstants.UPDATE_SALE_TAX,
            systemConstants.SPINNER_DEACTIVATE
          )
        );
      });
  };
};
const deleteSaleTax = (storeId, taxRateId) => {
  return (dispatch) => {
    dispatch(
      custom(storeConstants.DELETE_SALE_TAX, systemConstants.SPINNER_ACTIVATE)
    );
    storeService
      .deleteSaleTax(storeId, taxRateId)
      .then(
        (response) => {
          customToast(toastMessages.SALE_TAX_DELETED, toastType.SUCCESS);
          dispatch(getCustomSalesTaxRules(storeId));
        },
        (error) => {
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(
            storeConstants.DELETE_SALE_TAX,
            systemConstants.SPINNER_DEACTIVATE
          )
        );
      });
  };
};

const stripeCancelSubscription = (data) => {
  return (dispatch) => {
    dispatch(
      custom(
        dashboardConstants.UPDATE_SALE_TAX,
        systemConstants.SPINNER_ACTIVATE
      )
    );
    storeService
      .stripeCancelSubscription(data)
      .then(
        (response) => {
          dispatch(getStores(EMPTY_STRING));
          customToast(response.message, toastType.SUCCESS);
        },
        (error) => {
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(
            dashboardConstants.UPDATE_SALE_TAX,
            systemConstants.SPINNER_DEACTIVATE
          )
        );
      });
  };
};

const getUpdatedCurrentStoreDetails = (storeId, shouldUpdateStore = true) => {
  return (dispatch) => {
    dispatch(
      custom(storeConstants.GET_STORE_BY_ID, systemConstants.SPINNER_ACTIVATE)
    );
    storeService
      .getStoreById(storeId)
      .then(
        (response) => {
          // Set the current store based on the valid index
          if (shouldUpdateStore) {
            dispatch(success(response, storeConstants.SET_CURRENT_STORE));
          }

          dispatch(success(response, storeConstants.GET_STORE_BY_ID));
        },
        (error) => {
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(
            storeConstants.GET_STORE_BY_ID,
            systemConstants.SPINNER_DEACTIVATE
          )
        );
      });
  };
};

const triggerCustomerSyncWithShopify = (data) => {
  return (dispatch) => {
    dispatch(
      custom(storeConstants.GET_STORE_BY_ID, systemConstants.SPINNER_ACTIVATE)
    );
    storeService
      .triggerCustomerSyncWithShopify(data)
      .then(
        (response) => {
          dispatch(getUpdatedCurrentStoreDetails(data.storeId));
        },
        (error) => {
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(
            storeConstants.GET_STORE_BY_ID,
            systemConstants.SPINNER_DEACTIVATE
          )
        );
      });
  };
};

const addFreeMonths = (data, navigate) => {
  return (dispatch) => {
    dispatch(
      custom(storeConstants.ADD_FREE_MONTHS, systemConstants.SPINNER_ACTIVATE)
    );
    storeService
      .addFreeMonths(data)
      .then(
        (response) => {
          customToast(response.message, toastType.SUCCESS);
          handleEditStoreClick(response?.data, navigate); // make sure to update the state on edit store screen as user can click update after granting free months and the local data is stale so it will revert the free months update
        },
        (error) => {
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(
            storeConstants.ADD_FREE_MONTHS,
            systemConstants.SPINNER_DEACTIVATE
          )
        );
      });
  };
};

const getStripeCheckoutSession = (storeId) => {
  return (dispatch) => {
    dispatch(
      custom(
        storeConstants.GET_STRIPE_CHECKOUT_SESSION,
        systemConstants.SPINNER_ACTIVATE
      )
    );
    storeService
      .getStripeCheckoutSession(storeId)
      .then(
        (response) => {
          window.location.replace(response);
        },
        (error) => {
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(
            storeConstants.GET_STRIPE_CHECKOUT_SESSION,
            systemConstants.SPINNER_DEACTIVATE
          )
        );
      });
  };
};

const getStripeCustomerPortalSession = (storeId) => {
  return (dispatch) => {
    dispatch(
      custom(
        storeConstants.GET_STRIPE_CUSTOMER_PORTAL_SESSION,
        systemConstants.SPINNER_ACTIVATE
      )
    );
    storeService
      .getStripeCustomerPortalSession(storeId)
      .then(
        (response) => {
          window.location.replace(response);
        },
        (error) => {
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(
            storeConstants.GET_STRIPE_CUSTOMER_PORTAL_SESSION,
            systemConstants.SPINNER_DEACTIVATE
          )
        );
      });
  };
};

const getStoreMiscData = (storeId, type) => {
  return (dispatch) => {
    dispatch(
      custom(
        storeConstants.GET_STORE_MISC_DATA,
        systemConstants.SPINNER_ACTIVATE
      )
    );
    storeService
      .getStoreMiscData(storeId, type)
      .then(
        (response) => {
          dispatch(success(response, storeConstants.GET_STORE_MISC_DATA));
        },
        (error) => {
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(
            storeConstants.GET_STORE_MISC_DATA,
            systemConstants.SPINNER_DEACTIVATE
          )
        );
      });
  };
};

//-------Export Authentication Actions
export const storeActions = {
  getStores,
  addStore,
  editStore,
  uploadLogo,
  deleteStore,
  editSaleTax,
  getStoreOwners,
  deleteBlobByUrl,
  createNotification,
  updateStoreSettings,
  updateReceiptDetails,
  getUserNotifications,
  getPayoutsInformation,
  markNotificationAsRead,
  stripeCancelSubscription,
  getStripeCheckoutSession,
  getUpdatedCurrentStoreDetails,
  triggerCustomerSyncWithShopify,
  getStripeCustomerPortalSession,
  addFreeMonths,
  getCustomSalesTaxRules,
  addSaleTax,
  deleteSaleTax,
  getStoreMiscData,
};
